import { useState, useContext, useMemo, useEffect, useCallback } from "react";
import PrivateLayout from "@/components/layout/auth/PrivateLayout";
import { Button } from "@/components/ui/button";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useI18n } from "@/hooks/useI18n";
import { Calendar, Edit3, CheckCircle, CreditCard, Activity, Plus } from "lucide-react";

import { DashboardItinerary, DashboardTravelRequest } from "@/lib/models/dashboard";
import { TabsHeader } from "@/components/ui/tabs-header";
import { Tabs, ProfileTabsContent } from "@/components/ui/tabs";
import { useSidebar } from "@/contexts/SidebarContext";
import { AuthContext } from "@/lib/auth/models/auth.model";
import { useDashboardData } from "@/hooks/useDashboardData";
import { useItineraries } from "@/hooks/useItineraries";

import { DashboardItinerariesTab } from "@/lib/dashboard/DashboardItinerariesTab";
import { CreateItineraryFromRequestModal } from "@/lib/dashboard/CreateItineraryFromRequestModal";
import { ItineraryTabConfig } from "@/lib/dashboard/types";

const Dashboard = () => {
  const { t } = useI18n();

  // Pagina di soli itinerari: le richieste di viaggio vivono in /requests e non
  // servono nemmeno per le card, che rimandano lì con un link.
  const {
    itineraries: allItineraries,
    loading,
    error,
    refreshData,
  } = useDashboardData({ skipTravelRequests: true });

  const { deleteItinerary, duplicateItinerary, updateItineraryStatus } =
    useItineraries({ disableAutoFetch: true });

  const authContext = useContext(AuthContext);
  if (!authContext) throw new Error("Dashboard must be used inside an AuthProvider");
  const userRole = authContext.userRole;

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isSidebarCollapsed } = useSidebar();
  const activeTab = searchParams.get("tab") || (userRole === "traveler" ? "proposals" : "draft");

  // Le mail già spedite puntano a /dashboard?tab=requests&requestId=…: le
  // richieste ora hanno una pagina propria, quindi quei link vanno inoltrati.
  const legacyRequestId = searchParams.get("requestId");
  const wantsRequestsTab = searchParams.get("tab") === "requests";

  // ── Refresh flags ────────────────────────────────────────────────────────
  const [refreshingItineraries, setRefreshingItineraries] = useState(false);

  // Un itinerario parte sempre da una richiesta aperta: la si sceglie qui.
  const [isCreateItineraryModalOpen, setIsCreateItineraryModalOpen] = useState(false);

  // ── Derived itinerary lists (badge counts only) ──────────────────────────
  // La tab "Attivi" esiste solo per chi lavora sui viaggi. Il viaggiatore ha
  // "Proposte" per sended/request_changes e continua a vedere i confermati in
  // "Pianificati" anche col saldo a rate aperto: senza questo guardia-ruolo
  // sparirebbero da entrambe le sue tab.
  const hasActiveTab = userRole !== "traveler";

  // Il viaggiatore legge i propri viaggi, non li costruisce.
  const canCreateItinerary = userRole !== "traveler";
  const isActive = useCallback(
    (it: DashboardItinerary) => hasActiveTab && it.isActive,
    [hasActiveTab]
  );

  const activeItineraries = useMemo(() => allItineraries.filter(isActive), [allItineraries, isActive]);
  const draftItineraries = useMemo(() => allItineraries.filter((it) => it.status !== "completed" && !isActive(it)), [allItineraries, isActive]);
  const proposalsItineraries = useMemo(() => allItineraries.filter((it) => it.status === "sended" || it.status === "request_changes"), [allItineraries]);
  const plannedItineraries = useMemo(() => { return allItineraries.filter((it) => (it.detailedStatus === 'upcoming' || it.detailedStatus === 'ongoing') && !isActive(it)); }, [allItineraries, isActive]);
  const finishedItineraries = useMemo(() => { return allItineraries.filter((it) => it.detailedStatus === 'past'); }, [allItineraries]);

  // ── Refresh handlers ─────────────────────────────────────────────────────
  const handleRefreshItineraries = async () => {
    setRefreshingItineraries(true);
    await refreshData();
    setRefreshingItineraries(false);
  };

  const handleUpdateItineraryStatus = async (id: string, status: any) => {
    const result = await updateItineraryStatus(id, status);
    await refreshData();
    return result;
  };

  // Dal badge della richiesta su una card itinerario si passa alla pagina delle
  // richieste, che apre il dettaglio: qui i dati delle richieste non ci sono.
  const handleViewTravelRequest = (id: string) => navigate(`/requests?requestId=${id}`);

  // ── Itinerary action handlers ────────────────────────────────────────────
  const handleEditItinerary = (id: string) =>
    navigate(`/itinerary-builder?itineraryId=${id}`);

  const handleDeleteItinerary = async (id: string) => {
    const success = await deleteItinerary(id);
    if (success) await refreshData();
  };

  const handleDuplicateItinerary = async (id: string) => {
    await duplicateItinerary(id);
    await refreshData();
  };

  const handleTabChange = (value: string) => setSearchParams({ tab: value });

  const handleCreateItineraryFromRequest = (request: DashboardTravelRequest) => {
    setIsCreateItineraryModalOpen(false);
    navigate(`/itinerary-builder?requestId=${request.id}`);
  };

  useEffect(() => {
    if (!wantsRequestsTab && !legacyRequestId) {
      return;
    }
    navigate(
      legacyRequestId ? `/requests?requestId=${legacyRequestId}` : "/requests",
      { replace: true }
    );
  }, [wantsRequestsTab, legacyRequestId, navigate]);

  // ── Tab header config (role-aware, drives badge counts) ──────────────────
  const tabs = useMemo(() => {
    if (userRole === "traveler") {
      return [
        { value: "planned", label: t("dashboard.tabPlanned"), icon: Calendar, badge: plannedItineraries.length.toString() },
        { value: "proposals", label: t("dashboard.tabProposals"), icon: CreditCard, badge: proposalsItineraries.length.toString() },
        { value: "finished", label: t("dashboard.tabFinished"), icon: CheckCircle, badge: finishedItineraries.length.toString() },
      ];
    }
    return [
      { value: "draft", label: t("dashboard.tabDrafts"), icon: Edit3, badge: draftItineraries.length.toString() },
      { value: "active", label: t("dashboard.tabActive"), icon: Activity, badge: activeItineraries.length.toString() },
      { value: "planned", label: t("dashboard.tabPlanned"), icon: Calendar, badge: plannedItineraries.length.toString() },
      { value: "finished", label: t("dashboard.tabFinished"), icon: CheckCircle, badge: finishedItineraries.length.toString() },
    ];
  }, [userRole, t, draftItineraries.length, proposalsItineraries.length, activeItineraries.length, plannedItineraries.length, finishedItineraries.length]);

  // ── Itinerary tab configs (pure data – no state setters) ─────────────────
  const itineraryTabConfigs: ItineraryTabConfig[] = useMemo(() => {
    const all: ItineraryTabConfig[] = [
      {
        type: "itineraries",
        value: "draft",
        labelKey: "dashboard.tabDrafts",
        icon: Edit3,
        visibleTo: ["travel_designer", "travel_advisor_member", "admin", "agency"],
        data: draftItineraries,
        showDesignerFilter: true,
        i18n: {
          countKey: "dashboard.draftCount",
          noCountKey: "dashboard.noDraftItineraries",
          emptyTitleKey: "dashboard.noDraftItinerariesYet",
          emptyTitleDesignerKey: "dashboard.noDraftItinerariesForDesigner",
          emptyDescKey: "dashboard.createFirstItinerary",
          emptyDescDesignerKey: "dashboard.tryDifferentDesigner",
          errorKey: "dashboard.loadItinerariesError",
        },
      },
      {
        type: "itineraries",
        value: "proposals",
        labelKey: "dashboard.tabProposals",
        icon: CreditCard,
        visibleTo: ["traveler"],
        data: proposalsItineraries,
        showDesignerFilter: false,
        i18n: {
          countKey: "dashboard.proposalsCount",
          noCountKey: "dashboard.noProposalsItineraries",
          emptyTitleKey: "dashboard.noProposalsItineraries",
          errorKey: "dashboard.loadItinerariesError",
        },
      },
      {
        type: "itineraries",
        value: "active",
        labelKey: "dashboard.tabActive",
        icon: Activity,
        visibleTo: ["travel_designer", "travel_advisor_member", "admin", "agency"],
        data: activeItineraries,
        showDesignerFilter: true,
        i18n: {
          countKey: "dashboard.activeCount",
          noCountKey: "dashboard.noActiveItineraries",
          emptyTitleKey: "dashboard.noActiveItineraries",
          emptyTitleDesignerKey: "dashboard.noActiveItinerariesForDesigner",
          emptyDescKey: "dashboard.activeItinerariesDesc",
          emptyDescDesignerKey: "dashboard.tryDifferentDesigner",
          errorKey: "dashboard.loadItinerariesError",
        },
      },
      {
        type: "itineraries",
        value: "planned",
        labelKey: "dashboard.tabPlanned",
        icon: Calendar,
        visibleTo: ["travel_designer", "travel_advisor_member", "admin", "traveler", "agency"],
        data: plannedItineraries,
        showDesignerFilter: true,
        i18n: {
          countKey: "dashboard.plannedCount",
          noCountKey: "dashboard.noPlannedItineraries",
          emptyTitleKey: "dashboard.noPlannedItineraries",
          emptyTitleDesignerKey: "dashboard.noItinerariesForDesigner",
          emptyDescKey: "dashboard.completeFirstItinerary",
          emptyDescDesignerKey: "dashboard.tryDifferentDesigner",
          errorKey: "dashboard.loadCompletedError",
        },
      },
      {
        type: "itineraries",
        value: "finished",
        labelKey: "dashboard.tabFinished",
        icon: CheckCircle,
        visibleTo: ["travel_designer", "travel_advisor_member", "admin", "traveler", "agency"],
        data: finishedItineraries,
        showDesignerFilter: true,
        i18n: {
          countKey: "dashboard.finishedCount",
          noCountKey: "dashboard.noFinishedItineraries",
          emptyTitleKey: "dashboard.noFinishedItineraries",
          emptyTitleDesignerKey: "dashboard.noItinerariesForDesigner",
          errorKey: "dashboard.loadItinerariesError",
        },
      },
    ];
    return all.filter((cfg) => !cfg.visibleTo || cfg.visibleTo.includes(userRole ?? ""));
  }, [userRole, draftItineraries, proposalsItineraries, activeItineraries, plannedItineraries, finishedItineraries]);

  return (
    <PrivateLayout
      pageTitle={t("dashboard.pageTitle")}
      pageDescription={t("dashboard.pageDescription")}
      showBackButton={false}
      headerAction={
        canCreateItinerary ? (
          <Button size="lg" className="h-8 px-3" onClick={() => setIsCreateItineraryModalOpen(true)}>
            <Plus className="mr-1 h-4 w-4" />
            {t("dashboard.createItinerary")}
          </Button>
        ) : undefined
      }
      headerActionCompact={
        canCreateItinerary ? (
          <Button
            size="sm"
            className="h-8 flex-shrink-0 whitespace-nowrap rounded-full px-2.5 text-xs"
            onClick={() => setIsCreateItineraryModalOpen(true)}
          >
            <Plus className="mr-1 h-3.5 w-3.5" />
            {t("dashboard.createItinerary")}
          </Button>
        ) : undefined
      }
    >
      <div className="space-y-6">
        <TabsHeader
          activeTab={activeTab}
          onTabChange={handleTabChange}
          tabs={tabs}
          sidebarCollapsed={isSidebarCollapsed}
        />

        <div className="xl:px-8 md:px-4 mx-auto">
          <Tabs
            value={activeTab}
            onValueChange={handleTabChange}
            className="w-full"
          >
            {itineraryTabConfigs.map((cfg) => (
              <ProfileTabsContent key={cfg.value} value={cfg.value}>
                <DashboardItinerariesTab
                  config={cfg}
                  userRole={userRole}
                  loading={loading}
                  error={error}
                  onEdit={handleEditItinerary}
                  onDelete={handleDeleteItinerary}
                  onDuplicate={handleDuplicateItinerary}
                  onViewTravelRequest={handleViewTravelRequest}
                  onUpdateStatus={handleUpdateItineraryStatus}
                  onRefresh={handleRefreshItineraries}
                  refreshing={refreshingItineraries}
                />
              </ProfileTabsContent>
            ))}
          </Tabs>
        </div>
      </div>

      <CreateItineraryFromRequestModal
        isOpen={isCreateItineraryModalOpen}
        onClose={() => setIsCreateItineraryModalOpen(false)}
        onConfirm={handleCreateItineraryFromRequest}
      />
    </PrivateLayout>
  );
};

export default Dashboard;